import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Map as MapIcon, Download, Trash2, Copy } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const useImg = (id) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/map-posts/${id}/image`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [id]);
  return url;
};

export const downloadMapPost = async (id) => {
  try {
    const r = await api.get(`/map-posts/${id}/image`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `map-story-${id}.jpg`;
    a.click();
  } catch (e) { toast.error(errMsg(e)); }
};

const Draft = ({ p, onChange }) => {
  const img = useImg(p.id);
  const text = `${p.caption}\n.\n.\n${p.hashtags.join(" ")}`;
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid={`map-draft-${p.id}`} className="flex gap-4 bg-white border border-[#E2D9CC] p-4">
      {img ? <img src={img} alt={p.kind === "milestone" ? "Milestone post" : "Map story"} className={`w-24 ${p.kind === "milestone" || p.kind === "pack" ? "h-[120px]" : "h-[171px]"} object-cover border border-[#E2D9CC]`} /> : <div className="w-24 h-[171px] bg-[#EFE6F3]" />}
      <div className="flex-1 min-w-0 space-y-2">
        <p data-testid={`map-draft-label-${p.id}`} className="text-[11px] uppercase tracking-wider text-[#C59B27]">{p.kind === "pack" ? `Pack feed post · ${p.pack_name} · ${p.total} guides` : p.kind === "milestone" ? `Milestone feed post · ${p.region} reaches ${p.milestone} guides` : `Map Story · ${p.total} guides · new: ${p.new_towns.map((n) => n.town).join(", ")}`}</p>
        <p className="text-xs text-[#1C181D] line-clamp-3">{p.caption}</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" data-testid={`map-draft-copy-${p.id}`} onClick={() => { navigator.clipboard.writeText(text); toast.success("Caption copied"); }} className="rounded-none h-8"><Copy className="w-3.5 h-3.5 mr-1" />Copy caption</Button>
          <Button size="sm" variant="outline" data-testid={`map-draft-download-${p.id}`} onClick={() => downloadMapPost(p.id)} className="rounded-none h-8"><Download className="w-3.5 h-3.5 mr-1" />Download image</Button>
          <Button size="sm" variant="ghost" data-testid={`map-draft-delete-${p.id}`} onClick={() => act(() => api.delete(`/map-posts/${p.id}`), "Map story deleted")} className="rounded-none h-8 text-[#8C827A]"><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
    </div>
  );
};

export const MapDrafts = () => {
  const [posts, setPosts] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/map-posts").then((r) => setPosts(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const refresh = load;
  const make = async () => {
    setBusy(true);
    try { await api.post("/map-posts", {}); toast.success("Map story created"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="map-drafts" className="mb-10">
      <div className="flex items-center gap-4 mb-4">
        <h2 className="font-display text-2xl text-[#3B1B48] flex items-center gap-2"><MapIcon className="w-5 h-5 text-[#C59B27]" strokeWidth={1.4} />Map stories &amp; pack posts</h2>
        <p className="text-sm text-[#8C827A] flex-1">Made automatically whenever a guide is published (1080×1920 Story), plus a 1080×1350 feed post when a region reaches 5, 10 or 25 guides, and a feed post for every published pack.</p>
        <Button variant="outline" data-testid="make-map-post-button" onClick={make} disabled={busy} className="rounded-none">{busy ? "Creating…" : "Make map post"}</Button>
      </div>
      {!posts.length && <p className="text-sm text-[#8C827A]" data-testid="map-drafts-empty">No map stories yet. Publish a guide or click "Make map post".</p>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">{posts.slice(0, 6).map((p) => <Draft key={p.id} p={p} onChange={refresh} />)}</div>
    </div>
  );
};
